import React, { useState } from 'react';
import { API } from '../../api/client';
import { useApi } from '../../api/useApi';
import { useRole } from '../RoleContext';

interface Admin {
  id: number;
  email: string;
  name: string | null;
  createdAt: string | null;
  pending: boolean;
}

/**
 * Team Access — manage the set of users who can reach the admin surface. `admin`
 * is a multi-user role; existing admins add colleagues by email (promoting an
 * existing account, or pre-seeding a pending one that activates on first login)
 * and revoke access. The backend guards against self-removal and removing the
 * last admin. No Cognito account is created here — that lands with the invite
 * flow once auth is live.
 */
export default function AdminTeam() {
  const { user } = useRole();
  const [reloadKey, setReloadKey] = useState(0);
  const { data: admins, loading, error } = useApi<Admin[]>(() => API.listAdmins(), [reloadKey]);

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [rowError, setRowError] = useState('');

  const list: Admin[] = admins || [];

  const add = async () => {
    const value = email.trim();
    const first = firstName.trim();
    const last = lastName.trim();
    if (!value || !first || !last) return;
    setAdding(true);
    setAddError('');
    try {
      await API.addAdmin(value, first, last);
      setEmail('');
      setFirstName('');
      setLastName('');
      setReloadKey(k => k + 1);
    } catch (e: any) {
      setAddError(e?.message || 'Failed to add admin. Please try again.');
    } finally {
      setAdding(false);
    }
  };

  const canAdd = !!email.trim() && !!firstName.trim() && !!lastName.trim();

  const resend = async (a: Admin) => {
    setBusyId(a.id);
    setRowError('');
    try {
      await API.resendAdminInvite(a.id);
      window.alert(`Invite re-sent to ${a.email}.`);
    } catch (e: any) {
      setRowError(e?.message || 'Failed to resend the invite. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (a: Admin) => {
    if (!window.confirm(`Remove admin access for ${a.email}?`)) return;
    setBusyId(a.id);
    setRowError('');
    try {
      await API.removeAdmin(a.id);
      setReloadKey(k => k + 1);
    } catch (e: any) {
      setRowError(e?.message || 'Failed to remove admin. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="phil-page-heading">Team Access</div>
      <div className="phil-page-sub">Manage who can sign in to the FIL admin surface.</div>

      <div className="phil-card" style={{ marginTop: 20, marginBottom: 20 }}>
        <div className="phil-card-head">
          <h2>Add an administrator</h2>
          <span style={{ fontSize: 12, color: '#888' }}>Grants access to every admin page</span>
        </div>
        <div style={{ padding: '20px 24px' }}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
            Enter a FIL colleague’s name and email. If they already have an account it’s
            promoted to admin; otherwise we invite them — the account activates the first
            time they sign in.
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              className="phil-input"
              style={{ width: 150, padding: '6px 10px' }}
              type="text"
              placeholder="First name"
              value={firstName}
              onChange={e => { setFirstName(e.target.value); setAddError(''); }}
              onKeyDown={e => { if (e.key === 'Enter' && canAdd) add(); }}
            />
            <input
              className="phil-input"
              style={{ width: 150, padding: '6px 10px' }}
              type="text"
              placeholder="Last name"
              value={lastName}
              onChange={e => { setLastName(e.target.value); setAddError(''); }}
              onKeyDown={e => { if (e.key === 'Enter' && canAdd) add(); }}
            />
            <input
              className="phil-input"
              style={{ width: 320, padding: '6px 10px' }}
              type="email"
              placeholder="name@fil.org"
              value={email}
              onChange={e => { setEmail(e.target.value); setAddError(''); }}
              onKeyDown={e => { if (e.key === 'Enter' && canAdd) add(); }}
            />
            <button
              className="phil-btn phil-btn-primary phil-btn-sm"
              onClick={add}
              disabled={adding || !canAdd}
            >
              {adding ? 'Adding…' : 'Add admin'}
            </button>
          </div>
          {addError && <div className="phil-error" style={{ marginTop: 12 }}>{addError}</div>}
        </div>
      </div>

      <div className="phil-card">
        <div className="phil-card-head">
          <h2>Administrators &nbsp;<span style={{ fontSize: 12, fontWeight: 400, color: '#9ca3af' }}>({list.length})</span></h2>
        </div>
        <div style={{ padding: '8px 24px 20px' }}>
          {rowError && <div className="phil-error" style={{ margin: '12px 0' }}>{rowError}</div>}
          {loading ? (
            <div style={{ padding: '24px 0', color: '#888' }}>Loading…</div>
          ) : error ? (
            <div className="phil-error" style={{ margin: '12px 0' }}>Unable to load administrators. {error}</div>
          ) : list.length === 0 ? (
            <div style={{ padding: '24px 0', color: '#888' }}>No administrators yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9ca3af' }}>
                  <th style={{ padding: '10px 8px' }}>Name</th>
                  <th style={{ padding: '10px 8px' }}>Email</th>
                  <th style={{ padding: '10px 8px' }}>Status</th>
                  <th style={{ padding: '10px 8px', width: 100 }} />
                </tr>
              </thead>
              <tbody>
                {(() => { const activeCount = list.filter(x => !x.pending).length; return list.map(a => {
                  const isSelf = !!user && user.id === a.id;
                  // Only active (signed-in) admins count toward the minimum — removing a
                  // pending invitee never risks a lockout, but removing the sole active one does.
                  const isLastActive = !a.pending && activeCount <= 1;
                  const disabled = busyId === a.id || isSelf || isLastActive;
                  const reason = isSelf ? 'You can’t remove your own access' : isLastActive ? 'Can’t remove the last active admin' : '';
                  return (
                    <tr key={a.id} style={{ borderTop: '1px solid #eee' }}>
                      <td style={{ padding: '12px 8px', fontWeight: 600 }}>
                        {a.name || <span style={{ color: '#9ca3af', fontWeight: 400 }}>—</span>}
                        {isSelf && <span style={{ marginLeft: 8, fontSize: 11, color: '#9ca3af', fontWeight: 400 }}>(you)</span>}
                      </td>
                      <td style={{ padding: '12px 8px' }}>{a.email}</td>
                      <td style={{ padding: '12px 8px' }}>
                        {a.pending ? (
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#b45309', background: '#fef3c7', padding: '2px 8px', borderRadius: 12 }}>Invited · pending first sign-in</span>
                        ) : (
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#047857', background: '#d1fae5', padding: '2px 8px', borderRadius: 12 }}>Active</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 8px', display: 'flex', gap: 6 }}>
                        {!isSelf && (
                          <button
                            className="phil-btn phil-btn-secondary phil-btn-sm"
                            onClick={() => resend(a)}
                            disabled={busyId === a.id}
                            title="Re-send the Cognito invite (fresh temporary password)"
                          >
                            Resend invite
                          </button>
                        )}
                        <button
                          className="phil-btn phil-btn-secondary phil-btn-sm"
                          onClick={() => remove(a)}
                          disabled={disabled}
                          title={reason}
                        >
                          {busyId === a.id ? 'Removing…' : 'Remove'}
                        </button>
                      </td>
                    </tr>
                  );
                }); })()}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
